import jsPDF from "jspdf";
import "jspdf-autotable";
import { Row } from "../../components/list/NestedList";
import { jsPDFWithPlugin } from "./downloadTable";
import moment from "moment";
import AuthService from "../../services/auth/AuthService";
import bgPDF from "../../assets/images/backgroundPDF.png";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

export type RowData = { [key: string]: any };

type StyledCellObject = XLSX.CellObject & {
    s?: {
        font?: { bold?: boolean; sz?: number; color?: { rgb: string } };
        border?: {
            top?: { style: string; color: { rgb: string } };
            bottom?: { style: string; color: { rgb: string } };
            left?: { style: string; color: { rgb: string } };
            right?: { style: string; color: { rgb: string } };
        };
        fill?: { fgColor: { rgb: string }; patternType: string };
        alignment?: {
            horizontal?: "left" | "center" | "right";
            vertical?: "top" | "center" | "bottom";
            wrapText?: boolean;
        };
    };
};

type StyledWorksheet = XLSX.WorkSheet & {
    ["!cols"]?: XLSX.ColInfo[];
    ["!merges"]?: XLSX.Range[];
    ["!autofilter"]?: { ref: string };
    ["!freeze"]?: {
        xSplit: number;
        ySplit: number;
        topLeftCell: string;
        activePane: "bottomLeft" | "topRight" | "bottomRight" | "topLeft";
        state: "frozen";
    };
};

const asStyledSheet = (ws: XLSX.WorkSheet) => ws as StyledWorksheet;

function rgbaToRgb(rgba: string) {
    const parts = rgba
        .replace(/rgba?\(/, "")
        .replace(/\)/, "")
        .replace(/[\s+]/g, "")
        .split(",");
    const r = parseInt(parts[0], 10);
    const g = parseInt(parts[1], 10);
    const b = parseInt(parts[2], 10);
    return `rgb(${r}, ${g}, ${b})`;
}

class DownloadNestedList {
    private title: string;
    private rows: Row[];
    private columns: string[];
    private filters: string[];
    private summaryLabel: string;
    private detailsLabel: string;
    private noDataLabel: string;
    private infoLabel: string;
    private userLabel: string;
    private dateLabel: string;
    private hourLabel: string;
    private filterLabel: string;
    private propertyGroupLabel: string;
    private employeeLabel: string;
    private totalHoursLabel: string;
    private shiftLabel: string;

    private readonly HEADER_START_Y = 20;
    private readonly HEADER_START_X = 20;
    private readonly HEADER_FONT_SIZE = 14;
    private readonly PAGE_TOP_MARGIN = 45;
    private readonly NODE_TITLE_START_Y = 65;
    private readonly NODE_TITLE_START_X = 15;
    private readonly CONTENT_MARGIN_1 = 15;
    private readonly CONTENT_MARGIN_2 = 8;

    private readonly FOOTER_START_X = 14;
    private readonly FOOTER_START_Y = 6;
    private readonly FOOTER_FONT_SIZE = 14;
    private readonly TITLE_FONT_SIZE = 18;
    private readonly TABLE_BOTTOM_MARGIN = 35;
    private readonly TITLE_COLOR = "#1a77d2";
    protected readonly TEXT_COLOR = "#000000";
    protected readonly FOOTER_COLOR = "#000000";

    constructor(
        title: string,
        rows: Row[],
        columns: string[],
        summaryLabel: string,
        detailsLabel: string,
        noDataLabel: string,
        infoLabel: string,
        userLabel: string,
        dateLabel: string,
        hourLabel: string,
        filterLabel: string,
        propertyGroupLabel: string,
        employeeLabel: string,
        totalHoursLabel: string,
        shiftLabel: string,
        filters?: string[]
    ) {
        this.title = title;
        this.rows = rows;
        this.columns = columns;
        this.filters = filters || [];
        this.summaryLabel = summaryLabel;
        this.detailsLabel = detailsLabel;
        this.noDataLabel = noDataLabel;
        this.infoLabel = infoLabel;
        this.userLabel = userLabel;
        this.dateLabel = dateLabel;
        this.hourLabel = hourLabel;
        this.filterLabel = filterLabel;
        this.propertyGroupLabel = propertyGroupLabel;
        this.employeeLabel = employeeLabel;
        this.totalHoursLabel = totalHoursLabel;
        this.shiftLabel = shiftLabel;
    }

    async GetPDF() {
        const pdf = new jsPDF() as jsPDFWithPlugin;

        const bgEvent = this.addBackground(pdf);

        const headerEvent = this.addHeader(pdf);

        this.addMainTable(pdf);

        this.rows.forEach((row) => {
            this.addSubTable(pdf, row);
        });

        this.addFooter(pdf);

        pdf.internal.events.unsubscribe(bgEvent);
        pdf.internal.events.unsubscribe(headerEvent);

        pdf.save(`${this.title.toLowerCase().replaceAll(" ", "_")}.pdf`);
    }

    private addBackground(pdf: jsPDFWithPlugin) {
        var pdfBackground = new Image();
        pdfBackground.src = bgPDF;
        pdf.addImage(
            pdfBackground,
            "png",
            0,
            0,
            pdf.internal.pageSize.width,
            pdf.internal.pageSize.height
        );
        const bgEvent = pdf.internal.events.subscribe("addPage", () =>
            pdf.addImage(
                pdfBackground,
                "png",
                0,
                0,
                pdf.internal.pageSize.width,
                pdf.internal.pageSize.height
            )
        );

        return bgEvent;
    }

    private addHeader(pdf: jsPDFWithPlugin) {
        pdf.setFontSize(this.HEADER_FONT_SIZE);
        pdf.setTextColor(this.TEXT_COLOR);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const textWidth =
            (pdf.getStringUnitWidth(this.title) * pdf.getFontSize()) /
            pdf.internal.scaleFactor;
        pdf.text(
            this.title,
            pageWidth - textWidth - this.HEADER_START_X,
            this.HEADER_START_Y
        );
        pdf.setDrawColor(this.TEXT_COLOR);
        pdf.setLineWidth(0.4);
        pdf.line(
            pageWidth / 2,
            this.HEADER_START_Y + 3,
            pageWidth - this.HEADER_START_X,
            this.HEADER_START_Y + 3
        );

        const headerEvent = pdf.internal.events.subscribe("addPage", () => {
            pdf.setFontSize(this.HEADER_FONT_SIZE);
            pdf.setTextColor(this.TEXT_COLOR);
            const pageWidth = pdf.internal.pageSize.getWidth();
            const textWidth =
                (pdf.getStringUnitWidth(this.title) * pdf.getFontSize()) /
                pdf.internal.scaleFactor;
            pdf.text(
                this.title,
                pageWidth - textWidth - this.HEADER_START_X,
                this.HEADER_START_Y
            );
            pdf.setDrawColor(this.TEXT_COLOR);
            pdf.setLineWidth(0.4);
            pdf.line(
                pageWidth / 2,
                this.HEADER_START_Y + 3,
                pageWidth - this.HEADER_START_X,
                this.HEADER_START_Y + 3
            );
        });

        const firstColumn =
            `${this.userLabel}: ${AuthService.user?.name} \n` +
            `${this.dateLabel}: ${moment().utc().local().format("L")}\n` +
            `${this.hourLabel}: ${moment().utc().local().format("HH:mm").replace(":", "h")}`;
        const secondColumn =
            `${this.dateLabel}: ${this.filters[0]}\n` +
            `${this.propertyGroupLabel}: ${this.filters[1]}\n` +
            `${this.employeeLabel}: ${this.filters[2]}`;

        pdf.autoTable({
            body: [[firstColumn, secondColumn]],
            columns: [
                { header: this.infoLabel, dataKey: "info" },
                { header: this.filterLabel, dataKey: "filter" },
            ],
            startY: this.PAGE_TOP_MARGIN,
            theme: "plain",
            styles: { lineWidth: { right: 0.5 }, lineColor: [26, 119, 210] },
            headStyles: { cellPadding: { left: 5, top: 5, right: 5 } },
            bodyStyles: { cellPadding: { left: 5, bottom: 5, right: 5 } },
            tableLineColor: [26, 119, 210],
            tableLineWidth: 0.5,
        });

        return headerEvent;
    }

    private addMainTable(pdf: jsPDFWithPlugin) {
        pdf.setFontSize(this.TITLE_FONT_SIZE);
        pdf.setTextColor(this.TITLE_COLOR);

        const pageWidth = pdf.internal.pageSize.getWidth();
        const titleStartX =
            (pageWidth -
                (pdf.getStringUnitWidth(this.summaryLabel) *
                    pdf.getFontSize()) /
                    pdf.internal.scaleFactor) /
            2;
        pdf.text(
            this.summaryLabel,
            titleStartX,
            (pdf as any).lastAutoTable.finalY + this.CONTENT_MARGIN_1
        );

        pdf.autoTable({
            body: this.rows.map((row) => {
                const rowData = row.items.map((item) => item.toString());
                return rowData;
            }),
            startY:
                (pdf as any).lastAutoTable.finalY +
                this.CONTENT_MARGIN_1 +
                this.CONTENT_MARGIN_2,
            margin: { bottom: this.TABLE_BOTTOM_MARGIN },
        });
    }

    private addSubTable(pdf: jsPDFWithPlugin, row: Row) {
        pdf.addPage();

        const subtitle = `${row.items[0]}`;
        const pageWidth = pdf.internal.pageSize.getWidth();

        pdf.setFontSize(this.TITLE_FONT_SIZE);
        pdf.setTextColor(this.TITLE_COLOR);
        const subtitleStartX =
            (pageWidth -
                (pdf.getStringUnitWidth(subtitle) * pdf.getFontSize()) /
                    pdf.internal.scaleFactor) /
            2;
        pdf.text(subtitle, subtitleStartX, this.PAGE_TOP_MARGIN);

        if (!row.internalNodes) return;

        row.internalNodes.forEach((row, i) => {
            this.addNode(
                pdf,
                row,
                this.TITLE_FONT_SIZE - 2,
                i == 0
                    ? this.NODE_TITLE_START_Y
                    : (pdf as any).lastAutoTable.finalY + this.CONTENT_MARGIN_1
            );
        });
    }

    private addNode(
        pdf: jsPDFWithPlugin,
        row: Row,
        titleSize: number,
        startY: number
    ) {
        if (typeof row.items[0] === "string")
            row.items[0] = row.items[0].replace("➝", "->");
        const dateTitle = moment.isDate(row.items[0])
            ? moment.utc(row.items[0]).local().format("LL")
            : row.items[0];
        const nodeTitleLimit = pdf.internal.pageSize.height - 70;
        const nodeTableLimit = pdf.internal.pageSize.height - 80;

        if (row.internalNodes) {
            row.internalNodes.forEach((node, i) => {
                if (i != 0)
                    startY =
                        (pdf as any).lastAutoTable.finalY +
                        this.CONTENT_MARGIN_1;

                if (
                    startY - this.CONTENT_MARGIN_1 > nodeTitleLimit ||
                    (startY - this.CONTENT_MARGIN_1 > nodeTableLimit &&
                        node.leafNodes &&
                        node.leafNodes.length > 0)
                ) {
                    pdf.addPage();
                    startY = this.PAGE_TOP_MARGIN;
                }

                if (typeof node.items[0] === "string")
                    node.items[0] = node.items[0].replace("➝", "->");
                const hourTitle = moment.isDate(node.items[0])
                    ? moment.utc(node.items[0]).local().format("LL")
                    : node.items[0];
                const nodeTitle =
                    dateTitle.toString() + " " + hourTitle.toString();

                let color = node.color;
                if (color && color.includes("rgba")) color = rgbaToRgb(color);
                if (color) pdf.setTextColor(color);
                else pdf.setTextColor(this.TEXT_COLOR);

                pdf.setFontSize(titleSize);

                pdf.text(nodeTitle, this.NODE_TITLE_START_X, startY);

                if (node.leafNodes) {
                    pdf.autoTable({
                        columns: this.columns,
                        body: node.leafNodes.map((leafNode) => {
                            const rowData = leafNode.items.map((item) => {
                                return moment.isDate(item)
                                    ? moment.utc(item).local().format("LL")
                                    : item;
                            });

                            if (
                                leafNode.modalData &&
                                leafNode.modalData.length > 0
                            )
                                rowData.push("O");
                            else rowData.push("X");

                            return rowData;
                        }),
                        startY: startY + this.CONTENT_MARGIN_2,
                        margin: {
                            top: this.PAGE_TOP_MARGIN,
                            bottom: this.TABLE_BOTTOM_MARGIN,
                        },
                        pageBreak: "auto",
                    });
                } else {
                    pdf.autoTable({
                        body: [],
                        startY: startY + this.CONTENT_MARGIN_2,
                        margin: {
                            top: this.PAGE_TOP_MARGIN,
                            bottom: this.TABLE_BOTTOM_MARGIN,
                        },
                    });
                }
            });
        }
    }

    private addFooter(pdf: jsPDFWithPlugin) {
        pdf.setTextColor(this.FOOTER_COLOR);
        pdf.setFontSize(this.FOOTER_FONT_SIZE);
        const pageCount = pdf.internal.pages.length - 1;

        for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            const pagination = `${i}/${pageCount}`;
            const pageWidth = pdf.internal.pageSize.getWidth();

            // Right aligned text
            const textWidth =
                (pdf.getStringUnitWidth(pagination) * pdf.getFontSize()) /
                pdf.internal.scaleFactor;
            pdf.text(
                pagination,
                pageWidth - textWidth - this.FOOTER_START_X,
                pdf.internal.pageSize.getHeight() - this.FOOTER_START_Y
            );
        }
    }

    GetExcel() {
        const summaryHeader = [this.employeeLabel, this.totalHoursLabel];
        const summaryBody: (string | number)[][] = [];

        const detailsHeader = [
            this.employeeLabel,
            this.dateLabel,
            this.shiftLabel,
            this.columns?.[0],
            this.columns?.[1],
            this.columns?.[2],
            this.columns?.[3],
            this.columns?.[4],
            this.columns?.[5],
        ];
        const detailsBody: (string | number)[][] = [];

        if (this.rows?.length) {
            this.rows.forEach((employeeRow) => {
                const employeeName = String(employeeRow.items?.[0] ?? "");
                const totalHoursRaw = employeeRow.items?.[1];

                const totalHoursNumber =
                    typeof totalHoursRaw === "number"
                        ? Number(totalHoursRaw.toFixed(1))
                        : Number(
                              String(totalHoursRaw)
                                  .replace(/[^\d.,-]/g, "")
                                  .replace(",", ".")
                          );

                summaryBody.push([
                    employeeName,
                    isNaN(totalHoursNumber)
                        ? String(totalHoursRaw ?? "")
                        : totalHoursNumber,
                ]);

                employeeRow.internalNodes?.forEach((dateNode) => {
                    const dateLabel = moment.isDate(dateNode.items?.[0])
                        ? moment.utc(dateNode.items[0]).local().format("LL")
                        : String(dateNode.items?.[0] ?? "");

                    dateNode.internalNodes?.forEach((shiftNode) => {
                        const shiftLabel = String(shiftNode.items?.[0] ?? "");

                        shiftNode.leafNodes?.forEach((leaf) => {
                            const cells = leaf.items.map((item) =>
                                moment.isDate(item)
                                    ? moment.utc(item).local().format("LL")
                                    : String(item)
                            );

                            const hasComment = !!(
                                leaf.modalData && leaf.modalData.length
                            );
                            const comment = hasComment ? "O" : "X";

                            detailsBody.push([
                                employeeName,
                                dateLabel,
                                shiftLabel,
                                cells[0] ?? "",
                                cells[1] ?? "",
                                cells[2] ?? "",
                                cells[3] ?? "",
                                cells[4] ?? "",
                                comment,
                            ]);
                        });
                    });
                });
            });
        }

        const wb = XLSX.utils.book_new();

        const wsSummary = XLSX.utils.aoa_to_sheet([]);

        XLSX.utils.sheet_add_aoa(wsSummary, [[this.title]], { origin: "B1" });
        const titleCell = this.ensureStyledCell(wsSummary, "B1");
        titleCell.s = {
            font: { bold: true, sz: 20, color: { rgb: "1F497D" } },
            alignment: { horizontal: "center", vertical: "center" },
        };

        const merges: XLSX.Range[] =
            (wsSummary["!merges"] as XLSX.Range[] | undefined) ?? [];
        merges.push({ s: { r: 0, c: 1 }, e: { r: 0, c: 2 } });
        wsSummary["!merges"] = merges;

        XLSX.utils.sheet_add_aoa(
            wsSummary,
            [[this.infoLabel, this.filterLabel]],
            {
                origin: "B2",
            }
        );

        const infoHdr = this.ensureStyledCell(wsSummary, "B2");
        const filterHdr = this.ensureStyledCell(wsSummary, "C2");
        infoHdr.s = {
            ...(infoHdr.s ?? {}),
            font: { ...(infoHdr.s?.font ?? {}), bold: true },
        };
        filterHdr.s = {
            ...(filterHdr.s ?? {}),
            font: { ...(filterHdr.s?.font ?? {}), bold: true },
        };

        const infoRows = [
            [
                `${this.userLabel}: ${AuthService.user?.name ?? ""}`,
                `${this.dateLabel}: ${this.filters[0] ?? ""}`,
            ],
            [
                `${this.dateLabel}: ${moment().utc().local().format("LLL")}`,
                `${this.propertyGroupLabel}: ${this.filters[1] ?? ""}`,
            ],
            ["", `${this.employeeLabel}: ${this.filters[2] ?? ""}`],
        ];
        XLSX.utils.sheet_add_aoa(wsSummary, infoRows, { origin: "B3" });

        this.applyBoxBorder(wsSummary, 1, 4, 1, 2, "1A77D2");

        const SUMMARY_START_ROW = 8;
        const SUMMARY_START_COL = 1;
        const summaryTable = [summaryHeader, ...summaryBody];

        XLSX.utils.sheet_add_aoa(wsSummary, summaryTable, {
            origin: { r: SUMMARY_START_ROW - 1, c: SUMMARY_START_COL },
        });

        this.applyHeaderStyle(
            wsSummary,
            SUMMARY_START_ROW - 1,
            summaryHeader.length,
            SUMMARY_START_COL
        );
        this.autofitColumns(wsSummary, summaryTable, SUMMARY_START_COL);

        const summaryCols: XLSX.ColInfo[] =
            (wsSummary["!cols"] as XLSX.ColInfo[] | undefined) ?? [];
        summaryCols[1] = { wch: Math.max(summaryCols[1]?.wch ?? 0, 45) };
        summaryCols[2] = { wch: Math.max(summaryCols[2]?.wch ?? 0, 35) };
        wsSummary["!cols"] = summaryCols;

        this.freezeTopRow(wsSummary, SUMMARY_START_ROW - 1);
        this.addAutoFilter(
            wsSummary,
            SUMMARY_START_ROW - 1,
            SUMMARY_START_COL,
            summaryBody.length,
            SUMMARY_START_COL + summaryHeader.length - 1
        );

        if (summaryBody.length === 0) {
            XLSX.utils.sheet_add_aoa(wsSummary, [[this.noDataLabel]], {
                origin: { r: SUMMARY_START_ROW, c: SUMMARY_START_COL },
            });
            const noDataAddr = XLSX.utils.encode_cell({
                r: SUMMARY_START_ROW,
                c: SUMMARY_START_COL,
            });
            const noDataCell = this.ensureStyledCell(wsSummary, noDataAddr);
            noDataCell.s = {
                ...(noDataCell.s ?? {}),
                font: { ...(noDataCell.s?.font ?? {}), italic: true },
            };
        }

        XLSX.utils.book_append_sheet(wb, wsSummary, this.summaryLabel);

        const wsDetails = XLSX.utils.aoa_to_sheet([
            detailsHeader,
            ...detailsBody,
        ]);
        this.applyHeaderStyle(wsDetails, 0, detailsHeader.length, 0);
        this.autofitColumns(wsDetails, [detailsHeader, ...detailsBody], 0);
        this.freezeTopRow(wsDetails, 1);
        this.addAutoFilter(
            wsDetails,
            0,
            0,
            detailsBody.length,
            detailsHeader.length - 1
        );

        if (detailsBody.length === 0) {
            XLSX.utils.sheet_add_aoa(wsDetails, [[this.noDataLabel]], {
                origin: "A2",
            });
            const noDataCell = this.ensureStyledCell(wsDetails, "A2");
            noDataCell.s = {
                ...(noDataCell.s ?? {}),
                font: { ...(noDataCell.s?.font ?? {}), italic: true },
            };
        }

        XLSX.utils.book_append_sheet(wb, wsDetails, this.detailsLabel);

        const safeGroup = this.filters[1]
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^\w\-]+/g, "_");
        const safeDate = this.filters[0].replace(/[\s:]+/g, "_");
        const fileName = `${this.title.toLowerCase().replaceAll(" ", "_")}_${safeGroup}_${safeDate}.xlsx`;

        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
        });
        saveAs(blob, fileName);
    }

    private applyHeaderStyle(
        ws: XLSX.WorkSheet,
        headerRowIdx: number,
        colCount: number,
        startCol: number
    ) {
        const headerFont: NonNullable<StyledCellObject["s"]>["font"] = {
            bold: true,
            color: { rgb: "FFFFFF" },
        };
        const headerFill: NonNullable<StyledCellObject["s"]>["fill"] = {
            fgColor: { rgb: "1F497D" },
            patternType: "solid",
        };
        const headerAlign: NonNullable<StyledCellObject["s"]>["alignment"] = {
            vertical: "center",
            horizontal: "center",
            wrapText: true,
        };

        for (let offset = 0; offset < colCount; offset++) {
            const colIndex = startCol + offset;
            const addr = XLSX.utils.encode_cell({
                r: headerRowIdx,
                c: colIndex,
            });
            const cell = this.ensureStyledCell(ws, addr);
            cell.s = cell.s ?? {};
            cell.s.font = { ...(cell.s.font ?? {}), ...headerFont };
            cell.s.fill = headerFill;
            cell.s.alignment = { ...(cell.s.alignment ?? {}), ...headerAlign };
        }
    }

    private autofitColumns(
        ws: XLSX.WorkSheet,
        aoa: (string | number)[][],
        startCol: number
    ) {
        const MAX = 45;
        const sheet = asStyledSheet(ws);
        const cols: XLSX.ColInfo[] = sheet["!cols"] ?? [];

        aoa.forEach((row) => {
            row.forEach((val, j) => {
                const colIndex = startCol + j;
                const len = String(val ?? "").length;
                const next = Math.min(
                    Math.max(cols[colIndex]?.wch ?? 10, len + 2),
                    MAX
                );
                cols[colIndex] = { wch: next };
            });
        });

        sheet["!cols"] = cols;
    }

    private addAutoFilter(
        ws: XLSX.WorkSheet,
        headerRowIdx: number,
        firstColIdx: number,
        bodyLength: number,
        lastColIdx: number
    ) {
        const sheet = asStyledSheet(ws);
        const ref = XLSX.utils.encode_range({
            s: { r: headerRowIdx, c: firstColIdx },
            e: { r: headerRowIdx + bodyLength, c: lastColIdx },
        });
        sheet["!autofilter"] = { ref };
    }

    private freezeTopRow(ws: XLSX.WorkSheet, headerRowIdx: number) {
        const sheet = asStyledSheet(ws);
        sheet["!freeze"] = {
            xSplit: 0,
            ySplit: headerRowIdx,
            topLeftCell: XLSX.utils.encode_cell({ r: headerRowIdx + 1, c: 0 }),
            activePane: "bottomLeft",
            state: "frozen",
        };
    }

    private applyBoxBorder(
        ws: XLSX.WorkSheet,
        rowStart: number,
        rowEnd: number,
        columnStart: number,
        columnEnd: number,
        colorHex: string
    ) {
        const mk = (edge: "top" | "bottom" | "left" | "right") => ({
            style: "thin",
            color: { rgb: colorHex },
        });

        for (let rowIndex = rowStart; rowIndex <= rowEnd; rowIndex++) {
            for (
                let columnIndex = columnStart;
                columnIndex <= columnEnd;
                columnIndex++
            ) {
                const addrStr = XLSX.utils.encode_cell({
                    r: rowIndex,
                    c: columnIndex,
                });
                ws[addrStr] = ws[addrStr] || { t: "s", v: "" };
                const cell = ws[addrStr] as StyledCellObject;
                cell.s = cell.s || {};
                cell.s.border = cell.s.border || {};

                if (rowIndex === rowStart) cell.s.border.top = mk("top");
                if (rowIndex === rowEnd) cell.s.border.bottom = mk("bottom");
                if (columnIndex === columnStart)
                    cell.s.border.left = mk("left");
                if (columnIndex === columnEnd)
                    cell.s.border.right = mk("right");
            }
        }
    }

    private ensureStyledCell(
        ws: XLSX.WorkSheet,
        address: string
    ): StyledCellObject {
        if (!ws[address]) ws[address] = { t: "s", v: "" };
        return ws[address] as StyledCellObject;
    }
}

export default DownloadNestedList;
